import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Microphone recorder hook.
 *
 * Wraps `getUserMedia` + `MediaRecorder` with a tiny state machine. Designed
 * to work inside Telegram's WebView on both iOS and Android, which means:
 *
 *   - iOS Safari (and therefore Telegram-on-iOS) only reliably supports
 *     `audio/mp4` for `MediaRecorder`. Other browsers prefer `audio/webm;codecs=opus`.
 *     We probe for what's supported at start-up.
 *   - Permission must be requested in response to a user gesture (a tap).
 *     We do NOT auto-request on mount — `start()` must be called from an
 *     event handler.
 *   - We measure live RMS volume from a Web Audio AnalyserNode so the UI can
 *     show a "you're speaking" indicator. Pure visual sugar, no business logic.
 *
 * Caller flow:
 *   const rec = useAudioRecorder();
 *   await rec.start();      // call from a button click
 *   ... user speaks ...
 *   const blob = await rec.stop();   // returns a Blob in a Whisper-friendly mime
 *   // upload `blob`
 */

export interface UseAudioRecorder {
  state: 'idle' | 'starting' | 'recording' | 'stopping' | 'error'
  error: string | null
  // 0..1, updated ~30x/sec while recording. Use for a mic-volume meter.
  volume: number
  start(): Promise<void>
  stop(): Promise<Blob>
  cancel(): void
}

// Order matters: we pick the first mime the browser claims to support.
const PREFERRED_MIME_TYPES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4', // Safari / iOS
  'audio/mpeg',
]

function pickMimeType(): string | undefined {
  if (typeof MediaRecorder === 'undefined') return undefined
  for (const mime of PREFERRED_MIME_TYPES) {
    try {
      if (MediaRecorder.isTypeSupported(mime)) return mime
    } catch {
      // some browsers throw on unknown types — keep probing
    }
  }
  return undefined
}

export function useAudioRecorder(): UseAudioRecorder {
  const [state, setState] = useState<UseAudioRecorder['state']>('idle')
  const [error, setError] = useState<string | null>(null)
  const [volume, setVolume] = useState(0)

  const recorderRef = useRef<MediaRecorder | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const mimeRef = useRef<string | undefined>(undefined)
  const audioCtxRef = useRef<AudioContext | null>(null)
  const rafRef = useRef<number | null>(null)
  // The pending stop-resolver so callers can `await stop()` and get the Blob.
  const stopResolverRef = useRef<((blob: Blob) => void) | null>(null)

  const teardown = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current)
      rafRef.current = null
    }
    if (audioCtxRef.current) {
      void audioCtxRef.current.close()
      audioCtxRef.current = null
    }
    if (streamRef.current) {
      for (const track of streamRef.current.getTracks()) {
        track.stop()
      }
      streamRef.current = null
    }
    recorderRef.current = null
    chunksRef.current = []
  }, [])

  // Always tear down on unmount so we don't leak the mic if the user navigates
  // away mid-recording.
  useEffect(() => {
    return () => teardown()
  }, [teardown])

  const start = useCallback(async () => {
    setError(null)
    setState('starting')
    try {
      if (
        typeof navigator === 'undefined' ||
        !navigator.mediaDevices?.getUserMedia
      ) {
        throw new Error('Microphone is not available in this browser')
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          // Improves transcription quality.
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          channelCount: 1,
        },
      })
      streamRef.current = stream

      const mime = pickMimeType()
      mimeRef.current = mime
      const recorder = mime
        ? new MediaRecorder(stream, { mimeType: mime })
        : new MediaRecorder(stream)
      recorderRef.current = recorder
      chunksRef.current = []

      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) {
          chunksRef.current.push(e.data)
        }
      }

      recorder.onstop = () => {
        const finalMime = recorder.mimeType || mimeRef.current || 'audio/webm'
        const blob = new Blob(chunksRef.current, { type: finalMime })
        const resolver = stopResolverRef.current
        stopResolverRef.current = null
        teardown()
        setState('idle')
        setVolume(0)
        resolver?.(blob)
      }

      recorder.onerror = (event: Event) => {
        const err = (event as unknown as { error?: Error }).error
        setError(err?.message ?? 'Recording error')
        setState('error')
        teardown()
      }

      // Volume meter via Web Audio.
      const AudioCtx =
        window.AudioContext ??
        (window as unknown as { webkitAudioContext?: typeof AudioContext })
          .webkitAudioContext
      if (AudioCtx) {
        const ctx = new AudioCtx()
        audioCtxRef.current = ctx
        const src = ctx.createMediaStreamSource(stream)
        const analyser = ctx.createAnalyser()
        analyser.fftSize = 1024
        src.connect(analyser)
        const buf = new Uint8Array(analyser.fftSize)
        const tick = () => {
          analyser.getByteTimeDomainData(buf)
          // Compute RMS centred at 128 (silence). Normalise to 0..1.
          let sumSq = 0
          for (let i = 0; i < buf.length; i++) {
            const v = (buf[i]! - 128) / 128
            sumSq += v * v
          }
          const rms = Math.sqrt(sumSq / buf.length)
          setVolume(Math.min(1, rms * 4)) // boost so the UI has visible motion
          rafRef.current = requestAnimationFrame(tick)
        }
        rafRef.current = requestAnimationFrame(tick)
      }

      // IMPORTANT: do NOT pass a timeslice to start(). Fragmented webm chunks
      // produced by start(250) concatenate into a file that some decoders
      // (including OpenAI Whisper) reject as "Invalid file format" once the
      // recording exceeds ~10s. With start() we get a single dataavailable
      // event on stop and the resulting blob is a clean, well-formed webm.
      // Trade-off: we lose all audio if the page is killed mid-recording, but
      // for IELTS turns that's acceptable — the user just re-records.
      recorder.start()
      setState('recording')
    } catch (err) {
      setError((err as Error).message)
      setState('error')
      teardown()
      throw err
    }
  }, [teardown])

  const stop = useCallback(async () => {
    return new Promise<Blob>((resolve, reject) => {
      const recorder = recorderRef.current
      if (!recorder) {
        reject(new Error('Recorder is not running'))
        return
      }
      stopResolverRef.current = resolve
      setState('stopping')
      try {
        recorder.stop()
      } catch (err) {
        stopResolverRef.current = null
        teardown()
        setState('error')
        reject(err)
      }
    })
  }, [teardown])

  const cancel = useCallback(() => {
    teardown()
    setState('idle')
    setVolume(0)
    setError(null)
  }, [teardown])

  return { state, error, volume, start, stop, cancel }
}
